import { __decorate, __metadata } from 'tslib';
import * as i0 from '@angular/core';
import { InjectionToken, ChangeDetectorRef, TemplateRef, ViewContainerRef, Directive, Inject, Input, Pipe, DEFAULT_CURRENCY_CODE, LOCALE_ID, Injectable, NgModule } from '@angular/core';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, map as map$1 } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { getCurrencySymbol, formatCurrency as formatCurrency$1, formatDate, CommonModule } from '@angular/common';
import { interpolate } from '@domgen/dgx-fe-common';
import { ComponentStore } from '@ngrx/component-store';

var CountryIsoNumber;
(function (CountryIsoNumber) {
    CountryIsoNumber[CountryIsoNumber["GreatBritain"] = 826] = "GreatBritain";
    CountryIsoNumber[CountryIsoNumber["Ireland"] = 372] = "Ireland";
    CountryIsoNumber[CountryIsoNumber["UnitedStates"] = 840] = "UnitedStates";
})(CountryIsoNumber || (CountryIsoNumber = {}));

// https://github.com/validatorjs/validator.js/blob/master/src/lib/isPostalCode.js
const CountryPostalCodeFormat = {
    [CountryIsoNumber.GreatBritain]: new RegExp(/^(gir\s?0aa|[a-z]{1,2}\d[\da-z]?\s?(\d[a-z]{2})?)$/i),
    [CountryIsoNumber.Ireland]: new RegExp(/^(?!.*(?:o))[A-Za-z]\d[\dw]\s\w{4}$/i),
    [CountryIsoNumber.UnitedStates]: new RegExp(/^\d{5}(-\d{4})?$/),
};

const CountryTelephoneFormat = {
    [CountryIsoNumber.GreatBritain]: new RegExp(/^\d{10,11}$/),
    [CountryIsoNumber.Ireland]: new RegExp(/^\d{9,10}$/),
    [CountryIsoNumber.UnitedStates]: new RegExp(/^\+?\(?\d{3}\)?[-\s.]?\d{3}[-\s.]?\d{4,6}$/),
};

var IEProvinceCode;
(function (IEProvinceCode) {
    IEProvinceCode["Carlow"] = "CW";
    IEProvinceCode["Cavan"] = "CN";
    IEProvinceCode["Clare"] = "CE";
    IEProvinceCode["Cork"] = "CK";
    IEProvinceCode["Donegal"] = "DL";
    IEProvinceCode["Dublin"] = "DN";
    IEProvinceCode["Galway"] = "GY";
    IEProvinceCode["Kerry"] = "KY";
    IEProvinceCode["Kildare"] = "KE";
    IEProvinceCode["Kilkenny"] = "KK";
    IEProvinceCode["Laois"] = "LS";
    IEProvinceCode["Leitrim"] = "LM";
    IEProvinceCode["Limerick"] = "LK";
    IEProvinceCode["Longford"] = "LD";
    IEProvinceCode["Louth"] = "LH";
    IEProvinceCode["Mayo"] = "MO";
    IEProvinceCode["Meath"] = "MH";
    IEProvinceCode["Monaghan"] = "MN";
    IEProvinceCode["Offaly"] = "OY";
    IEProvinceCode["Roscommon"] = "RN";
    IEProvinceCode["Sligo"] = "SO";
    IEProvinceCode["Tipperary"] = "TY";
    IEProvinceCode["Waterford"] = "WD";
    IEProvinceCode["Westmeath"] = "WH";
    IEProvinceCode["Wexford"] = "WX";
    IEProvinceCode["Wicklow"] = "WW";
})(IEProvinceCode || (IEProvinceCode = {}));

const IEProvinceName = new Map([
    [IEProvinceCode.Carlow, 'Carlow'],
    [IEProvinceCode.Cavan, 'Cavan'],
    [IEProvinceCode.Clare, 'Clare'],
    [IEProvinceCode.Cork, 'Cork'],
    [IEProvinceCode.Donegal, 'Donegal'],
    [IEProvinceCode.Dublin, 'Dublin'],
    [IEProvinceCode.Galway, 'Galway'],
    [IEProvinceCode.Kerry, 'Kerry'],
    [IEProvinceCode.Kildare, 'Kildare'],
    [IEProvinceCode.Kilkenny, 'Kilkenny'],
    [IEProvinceCode.Laois, 'Laois'],
    [IEProvinceCode.Leitrim, 'Leitrim'],
    [IEProvinceCode.Limerick, 'Limerick'],
    [IEProvinceCode.Longford, 'Longford'],
    [IEProvinceCode.Louth, 'Louth'],
    [IEProvinceCode.Mayo, 'Mayo'],
    [IEProvinceCode.Meath, 'Meath'],
    [IEProvinceCode.Monaghan, 'Monaghan'],
    [IEProvinceCode.Offaly, 'Offaly'],
    [IEProvinceCode.Roscommon, 'Roscommon'],
    [IEProvinceCode.Sligo, 'Sligo'],
    [IEProvinceCode.Tipperary, 'Tipperary'],
    [IEProvinceCode.Waterford, 'Waterford'],
    [IEProvinceCode.Westmeath, 'Westmeath'],
    [IEProvinceCode.Wexford, 'Wexford'],
    [IEProvinceCode.Wicklow, 'Wicklow'],
]);

var USProvinceCode;
(function (USProvinceCode) {
    USProvinceCode["Alabama"] = "AL";
    USProvinceCode["Alaska"] = "AK";
    USProvinceCode["AmericanSamoa"] = "AS";
    USProvinceCode["Arizona"] = "AZ";
    USProvinceCode["Arkansas"] = "AR";
    USProvinceCode["California"] = "CA";
    USProvinceCode["Colorado"] = "CO";
    USProvinceCode["Connecticut"] = "CT";
    USProvinceCode["Delaware"] = "DE";
    USProvinceCode["DistrictOfColumbia"] = "DC";
    USProvinceCode["Florida"] = "FL";
    USProvinceCode["Georgia"] = "GA";
    USProvinceCode["Guam"] = "GU";
    USProvinceCode["Hawaii"] = "HI";
    USProvinceCode["Idaho"] = "ID";
    USProvinceCode["Illinois"] = "IL";
    USProvinceCode["Indiana"] = "IN";
    USProvinceCode["Iowa"] = "IA";
    USProvinceCode["Kansas"] = "KS";
    USProvinceCode["Kentucky"] = "KY";
    USProvinceCode["Louisiana"] = "LA";
    USProvinceCode["Maine"] = "ME";
    USProvinceCode["Maryland"] = "MD";
    USProvinceCode["Massachusetts"] = "MA";
    USProvinceCode["Michigan"] = "MI";
    USProvinceCode["Minnesota"] = "MN";
    USProvinceCode["Mississippi"] = "MS";
    USProvinceCode["Missouri"] = "MO";
    USProvinceCode["Montana"] = "MT";
    USProvinceCode["Nebraska"] = "NE";
    USProvinceCode["Nevada"] = "NV";
    USProvinceCode["NewHampshire"] = "NH";
    USProvinceCode["NewJersey"] = "NJ";
    USProvinceCode["NewMexico"] = "NM";
    USProvinceCode["NewYork"] = "NY";
    USProvinceCode["NorthCarolina"] = "NC";
    USProvinceCode["NorthDakota"] = "ND";
    USProvinceCode["NorthernMarianaIslands"] = "MP";
    USProvinceCode["Ohio"] = "OH";
    USProvinceCode["Oklahoma"] = "OK";
    USProvinceCode["Oregon"] = "OR";
    USProvinceCode["Pennsylvania"] = "PA";
    USProvinceCode["PuertoRico"] = "PR";
    USProvinceCode["RhodeIsland"] = "RI";
    USProvinceCode["SouthCarolina"] = "SC";
    USProvinceCode["SouthDakota"] = "SD";
    USProvinceCode["Tennessee"] = "TN";
    USProvinceCode["Texas"] = "TX";
    USProvinceCode["UnitedStatesMinorOutlyingIslands"] = "UM";
    USProvinceCode["Utah"] = "UT";
    USProvinceCode["Vermont"] = "VT";
    USProvinceCode["Virginia"] = "VA";
    USProvinceCode["VirginIslands"] = "VI";
    USProvinceCode["Washington"] = "WA";
    USProvinceCode["WestVirginia"] = "WV";
    USProvinceCode["Wisconsin"] = "WI";
    USProvinceCode["Wyoming"] = "WY";
})(USProvinceCode || (USProvinceCode = {}));

const USProvinceName = new Map([
    [USProvinceCode.Alabama, 'Alabama'],
    [USProvinceCode.Alaska, 'Alaska'],
    [USProvinceCode.AmericanSamoa, 'American Samoa'],
    [USProvinceCode.Arizona, 'Arizona'],
    [USProvinceCode.Arkansas, 'Arkansas'],
    [USProvinceCode.California, 'California'],
    [USProvinceCode.Colorado, 'Colorado'],
    [USProvinceCode.Connecticut, 'Connecticut'],
    [USProvinceCode.Delaware, 'Delaware'],
    [USProvinceCode.DistrictOfColumbia, 'District of Columbia'],
    [USProvinceCode.Florida, 'Florida'],
    [USProvinceCode.Georgia, 'Georgia'],
    [USProvinceCode.Guam, 'Guam'],
    [USProvinceCode.Hawaii, 'Hawaii'],
    [USProvinceCode.Idaho, 'Idaho'],
    [USProvinceCode.Illinois, 'Illinois'],
    [USProvinceCode.Indiana, 'Indiana'],
    [USProvinceCode.Iowa, 'Iowa'],
    [USProvinceCode.Kansas, 'Kansas'],
    [USProvinceCode.Kentucky, 'Kentucky'],
    [USProvinceCode.Louisiana, 'Louisiana'],
    [USProvinceCode.Maine, 'Maine'],
    [USProvinceCode.Maryland, 'Maryland'],
    [USProvinceCode.Massachusetts, 'Massachusetts'],
    [USProvinceCode.Michigan, 'Michigan'],
    [USProvinceCode.Minnesota, 'Minnesota'],
    [USProvinceCode.Mississippi, 'Mississippi'],
    [USProvinceCode.Missouri, 'Missouri'],
    [USProvinceCode.Montana, 'Montana'],
    [USProvinceCode.Nebraska, 'Nebraska'],
    [USProvinceCode.Nevada, 'Nevada'],
    [USProvinceCode.NewHampshire, 'New Hampshire'],
    [USProvinceCode.NewJersey, 'New Jersey'],
    [USProvinceCode.NewMexico, 'New Mexico'],
    [USProvinceCode.NewYork, 'New York'],
    [USProvinceCode.NorthCarolina, 'North Carolina'],
    [USProvinceCode.NorthDakota, 'North Dakota'],
    [USProvinceCode.NorthernMarianaIslands, 'Northern Mariana Islands'],
    [USProvinceCode.Ohio, 'Ohio'],
    [USProvinceCode.Oklahoma, 'Oklahoma'],
    [USProvinceCode.Oregon, 'Oregon'],
    [USProvinceCode.Pennsylvania, 'Pennsylvania'],
    [USProvinceCode.PuertoRico, 'Puerto Rico'],
    [USProvinceCode.RhodeIsland, 'Rhode Island'],
    [USProvinceCode.SouthCarolina, 'South Carolina'],
    [USProvinceCode.SouthDakota, 'South Dakota'],
    [USProvinceCode.Tennessee, 'Tennessee'],
    [USProvinceCode.Texas, 'Texas'],
    [USProvinceCode.UnitedStatesMinorOutlyingIslands, 'United States Minor Outlying Islands'],
    [USProvinceCode.Utah, 'Utah'],
    [USProvinceCode.Vermont, 'Vermont'],
    [USProvinceCode.VirginIslands, 'Virgin Islands'],
    [USProvinceCode.Virginia, 'Virginia'],
    [USProvinceCode.Washington, 'Washington'],
    [USProvinceCode.WestVirginia, 'West Virginia'],
    [USProvinceCode.Wisconsin, 'Wisconsin'],
    [USProvinceCode.Wyoming, 'Wyoming'],
]);

const COUNTRY_ISO_NUMBER = new InjectionToken('Country ISO Number');

let CountryIsoNumberDirective = class CountryIsoNumberDirective {
    constructor(countryIsoNumber, changeDetectorRef, templateRef, viewContainer) {
        this.countryIsoNumber = countryIsoNumber;
        this.changeDetectorRef = changeDetectorRef;
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.additionalConditionSubject = new BehaviorSubject(true);
        this.countryIsoNumbersSubject = new BehaviorSubject([]);
        this.isCountryIsoNumber$ = this.countryIsoNumbersSubject.pipe(map((countryIsoNumbers) => countryIsoNumbers.includes(this.countryIsoNumber)));
    }
    set dgxI18nCountryIsoNumber(countryIsoNumbers) {
        this.countryIsoNumbersSubject.next(Array.isArray(countryIsoNumbers) ? countryIsoNumbers : [countryIsoNumbers]);
    }
    set dgxI18nCountryIsoNumberAnd(additionalCondition) {
        this.additionalConditionSubject.next(Boolean(additionalCondition));
    }
    ngOnInit() {
        this.determineVisibility();
    }
    determineVisibility() {
        this.viewContainer.clear();
        combineLatest([this.additionalConditionSubject, this.isCountryIsoNumber$])
            .pipe(map((conditions) => conditions.every(Boolean)), distinctUntilChanged(), untilDestroyed(this))
            .subscribe((isCountryIsoNumber) => {
            this.viewContainer.clear();
            if (isCountryIsoNumber) {
                this.viewContainer.createEmbeddedView(this.templateRef);
            }
            else if (this.dgxI18nCountryIsoNumberElse) {
                this.viewContainer.createEmbeddedView(this.dgxI18nCountryIsoNumberElse);
            }
            this.changeDetectorRef.detectChanges();
        });
    }
};
CountryIsoNumberDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryIsoNumberDirective, deps: [{ token: COUNTRY_ISO_NUMBER }, { token: i0.ChangeDetectorRef }, { token: i0.TemplateRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
CountryIsoNumberDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.12", type: CountryIsoNumberDirective, selector: "[dgxI18nCountryIsoNumber]", inputs: { dgxI18nCountryIsoNumber: "dgxI18nCountryIsoNumber", dgxI18nCountryIsoNumberAnd: "dgxI18nCountryIsoNumberAnd", dgxI18nCountryIsoNumberElse: "dgxI18nCountryIsoNumberElse" }, ngImport: i0 });
CountryIsoNumberDirective = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [Number, ChangeDetectorRef,
        TemplateRef,
        ViewContainerRef])
], CountryIsoNumberDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryIsoNumberDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dgxI18nCountryIsoNumber]',
                }]
        }], ctorParameters: function () { return [{ type: CountryIsoNumber, decorators: [{
                    type: Inject,
                    args: [COUNTRY_ISO_NUMBER]
                }] }, { type: i0.ChangeDetectorRef }, { type: i0.TemplateRef }, { type: i0.ViewContainerRef }]; }, propDecorators: { dgxI18nCountryIsoNumber: [{
                type: Input
            }], dgxI18nCountryIsoNumberAnd: [{
                type: Input
            }], dgxI18nCountryIsoNumberElse: [{
                type: Input
            }] } });

var CountryIso2Code;
(function (CountryIso2Code) {
    CountryIso2Code["GreatBritain"] = "GB";
    CountryIso2Code["Ireland"] = "IE";
    CountryIso2Code["UnitedStates"] = "US";
})(CountryIso2Code || (CountryIso2Code = {}));

function countryIso2CodeFactory(countryIsoNumber) {
    switch (countryIsoNumber) {
        case CountryIsoNumber.Ireland:
            return CountryIso2Code.Ireland;
        case CountryIsoNumber.UnitedStates:
            return CountryIso2Code.UnitedStates;
        case CountryIsoNumber.GreatBritain:
        default:
            return CountryIso2Code.GreatBritain;
    }
}

var CountryIso3Code;
(function (CountryIso3Code) {
    CountryIso3Code["GreatBritain"] = "GBR";
    CountryIso3Code["Ireland"] = "IRL";
    CountryIso3Code["UnitedStates"] = "USA";
})(CountryIso3Code || (CountryIso3Code = {}));

function countryIso3CodeFactory(countryIsoNumber) {
    switch (countryIsoNumber) {
        case CountryIsoNumber.Ireland:
            return CountryIso3Code.Ireland;
        case CountryIsoNumber.UnitedStates:
            return CountryIso3Code.UnitedStates;
        case CountryIsoNumber.GreatBritain:
        default:
            return CountryIso3Code.GreatBritain;
    }
}

function countryIsoNumberFactory(countryIso) {
    switch (countryIso) {
        case CountryIso2Code.Ireland:
        case CountryIso3Code.Ireland:
        case CountryIsoNumber.Ireland:
            return CountryIsoNumber.Ireland;
        case CountryIso2Code.UnitedStates:
        case CountryIso3Code.UnitedStates:
        case CountryIsoNumber.UnitedStates:
            return CountryIsoNumber.UnitedStates;
        case CountryIso2Code.GreatBritain:
        case CountryIso3Code.GreatBritain:
        case CountryIsoNumber.GreatBritain:
        default:
            return CountryIsoNumber.GreatBritain;
    }
}

var CountryName;
(function (CountryName) {
    CountryName["GreatBritain"] = "Great Britain";
    CountryName["Ireland"] = "Ireland";
    CountryName["UnitedStates"] = "United States of America";
})(CountryName || (CountryName = {}));

function countryNameFactory(countryIsoNumber) {
    switch (countryIsoNumber) {
        case CountryIsoNumber.Ireland:
            return CountryName.Ireland;
        case CountryIsoNumber.UnitedStates:
            return CountryName.UnitedStates;
        case CountryIsoNumber.GreatBritain:
        default:
            return CountryName.GreatBritain;
    }
}

var CurrencyCode;
(function (CurrencyCode) {
    CurrencyCode["Euro"] = "EUR";
    CurrencyCode["PoundSterling"] = "GBP";
    CurrencyCode["USDollar"] = "USD";
})(CurrencyCode || (CurrencyCode = {}));

function defaultCurrencyCodeFactory(countryIsoNumber) {
    switch (countryIsoNumber) {
        case CountryIsoNumber.Ireland:
            return CurrencyCode.Euro;
        case CountryIsoNumber.UnitedStates:
            return CurrencyCode.USDollar;
        case CountryIsoNumber.GreatBritain:
        default:
            return CurrencyCode.PoundSterling;
    }
}

var CountryDateFormat;
(function (CountryDateFormat) {
    CountryDateFormat["GreatBritain"] = "dd/MM/yyyy";
    CountryDateFormat["Ireland"] = "dd/MM/yyyy";
    CountryDateFormat["UnitedStates"] = "MM/dd/yyyy";
})(CountryDateFormat || (CountryDateFormat = {}));

function defaultDateFormatFactory(countryIsoNumber) {
    switch (countryIsoNumber) {
        case CountryIsoNumber.Ireland:
            return CountryDateFormat.Ireland;
        case CountryIsoNumber.UnitedStates:
            return CountryDateFormat.UnitedStates;
        case CountryIsoNumber.GreatBritain:
        default:
            return CountryDateFormat.GreatBritain;
    }
}

function isGdprLocaleFactory(countryIsoNumber) {
    switch (countryIsoNumber) {
        case CountryIsoNumber.GreatBritain:
        case CountryIsoNumber.Ireland:
            return true;
        case CountryIsoNumber.UnitedStates:
        default:
            return false;
    }
}

var LocaleId;
(function (LocaleId) {
    LocaleId["English_GreatBritain"] = "en-GB";
    LocaleId["English_Ireland"] = "en-IE";
    LocaleId["English_US"] = "en-US";
    LocaleId["Portuguese_Brazil"] = "pt-BR";
    LocaleId["Portuguese_Portugal"] = "pt-PT";
    LocaleId["Spanish_Spain"] = "es-ES";
    LocaleId["Italian_Italy"] = "it-IT";
    LocaleId["French_France"] = "fr-FR";
})(LocaleId || (LocaleId = {}));

function localeIdFactory(countryIsoNumber, localeId) {
    if (localeId) {
        return localeId;
    }
    switch (countryIsoNumber) {
        case CountryIsoNumber.Ireland:
            return LocaleId.English_Ireland;
        case CountryIsoNumber.UnitedStates:
            return LocaleId.English_US;
        case CountryIsoNumber.GreatBritain:
        default:
            return LocaleId.English_GreatBritain;
    }
}

function provinceNamesFactory(countryIsoNumber) {
    switch (countryIsoNumber) {
        case CountryIsoNumber.UnitedStates:
            return USProvinceName;
        case CountryIsoNumber.Ireland:
            return IEProvinceName;
        case CountryIsoNumber.GreatBritain:
        default:
            return;
    }
}

function buildConjunction(items, conjunction) {
    if (items.length === 0) {
        return '';
    }
    if (items.length === 1) {
        return items[0];
    }
    return `${items.slice(0, -1).join(', ')} ${conjunction} ${items[items.length - 1]}`;
}

class ConjunctionPipe {
    transform(value, conjunction) {
        return buildConjunction(value, conjunction);
    }
}
ConjunctionPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ConjunctionPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
ConjunctionPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ConjunctionPipe, name: "conjunction" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ConjunctionPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'conjunction',
                }]
        }] });

class CurrencyFormatPipe {
    constructor(currencyCode, localeId) {
        this.currencyCode = currencyCode;
        this.localeId = localeId;
    }
    transform(value, digitsInfo = '1.2-2') {
        return formatCurrency(value, this.currencyCode, this.localeId, digitsInfo);
    }
}
CurrencyFormatPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CurrencyFormatPipe, deps: [{ token: DEFAULT_CURRENCY_CODE }, { token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Pipe });
CurrencyFormatPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CurrencyFormatPipe, name: "currencyFormat" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CurrencyFormatPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'currencyFormat',
                }]
        }], ctorParameters: function () { return [{ type: CurrencyCode, decorators: [{
                    type: Inject,
                    args: [DEFAULT_CURRENCY_CODE]
                }] }, { type: LocaleId, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; } });
function formatCurrency(value, currencyCode, localeId, digitsInfo = '1.2-2') {
    if (value === undefined || value === null || value === '') {
        return '';
    }
    const currencySymbol = getCurrencySymbol(currencyCode, 'wide');
    return formatCurrency$1(Number(value), localeId, currencySymbol, currencyCode, digitsInfo);
}

const DEFAULT_DATE_FORMAT = new InjectionToken('Default Date Format');

class DateFormatPipe {
    constructor(defaultDateFormat, localeId) {
        this.defaultDateFormat = defaultDateFormat;
        this.localeId = localeId;
    }
    transform(value, dateFormat = this.defaultDateFormat) {
        if (!value) {
            return '';
        }
        return formatDate(value, dateFormat, this.localeId);
    }
}
DateFormatPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DateFormatPipe, deps: [{ token: DEFAULT_DATE_FORMAT }, { token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Pipe });
DateFormatPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: DateFormatPipe, name: "dateFormat" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DateFormatPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dateFormat',
                }]
        }], ctorParameters: function () { return [{ type: CountryDateFormat, decorators: [{
                    type: Inject,
                    args: [DEFAULT_DATE_FORMAT]
                }] }, { type: LocaleId, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; } });

const PROVINCE_NAMES = new InjectionToken('Province names token');

function formatProvinceCode(provinceName, provinceNames) {
    provinceName = provinceName?.trim().replace(/\s+/g, ' ').toLowerCase();
    if (!provinceName || !provinceNames) {
        return;
    }
    if (provinceName.length === 2) {
        return provinceName.toUpperCase();
    }
    const [provinceCode] = Array.from(provinceNames.entries()).find(([, value]) => value.toLowerCase() === provinceName) ?? [];
    return provinceCode;
}
function formatProvinceName(provinceCode, provinceNames) {
    if (!provinceCode || !provinceNames) {
        return;
    }
    return provinceNames.get(provinceCode);
}

class I18nService {
    constructor(provinceNames) {
        this.provinceNames = provinceNames;
    }
    formatProvinceCode(provinceName) {
        return formatProvinceCode(provinceName, this.provinceNames);
    }
    formatProvinceName(provinceCode) {
        return formatProvinceName(provinceCode, this.provinceNames);
    }
}
I18nService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: I18nService, deps: [{ token: PROVINCE_NAMES }], target: i0.ɵɵFactoryTarget.Injectable });
I18nService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: I18nService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: I18nService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PROVINCE_NAMES]
                }] }]; } });

class ProvinceCodePipe {
    constructor(i18nService) {
        this.i18nService = i18nService;
    }
    transform(provinceName) {
        return this.i18nService.formatProvinceCode(provinceName) ?? provinceName ?? '';
    }
}
ProvinceCodePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProvinceCodePipe, deps: [{ token: I18nService }], target: i0.ɵɵFactoryTarget.Pipe });
ProvinceCodePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ProvinceCodePipe, name: "provinceCode" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProvinceCodePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'provinceCode',
                }]
        }], ctorParameters: function () { return [{ type: I18nService }]; } });

class ProvinceNamePipe {
    constructor(i18nService) {
        this.i18nService = i18nService;
    }
    transform(provinceCode) {
        return this.i18nService.formatProvinceName(provinceCode) ?? provinceCode ?? '';
    }
}
ProvinceNamePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProvinceNamePipe, deps: [{ token: I18nService }], target: i0.ɵɵFactoryTarget.Pipe });
ProvinceNamePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ProvinceNamePipe, name: "provinceName" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProvinceNamePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'provinceName',
                }]
        }], ctorParameters: function () { return [{ type: I18nService }]; } });

const TRANSLATION_DICTIONARY_MAPPING = new InjectionToken('Translation dictionary mapping token');

function provideTranslationDictionaryMapping(translationDictionaryMapping) {
    return [
        TranslateService,
        {
            provide: TRANSLATION_DICTIONARY_MAPPING,
            useValue: translationDictionaryMapping ?? {},
            multi: true,
        },
    ];
}
function mergeTranslationDictionaryMappings(...translationDictionaryMappings) {
    const localeIds = [
        ...new Set(translationDictionaryMappings.reduce((keys, translationDictionaryMapping) => [
            ...keys,
            ...Object.keys(translationDictionaryMapping),
        ], [])),
    ];
    return localeIds.reduce((mergedTranslationMappings, localeId) => {
        const localeTranslationDictionaries = translationDictionaryMappings.map((translationDictionaryMapping) => translationDictionaryMapping[localeId]);
        return {
            ...mergedTranslationMappings,
            [localeId]: mergeTranslationDictionaries(...localeTranslationDictionaries),
        };
    }, {});
}
function mergeTranslationDictionaries(...translationDictionaries) {
    return translationDictionaries.reduce((target, source) => ({
        ...target,
        ...(source ?? {}),
    }), {});
}

class TranslationDictionaryStore extends ComponentStore {
    constructor(localeId) {
        super({
            localeId,
            translationDictionaryMapping: {},
        });
        this.localeId = localeId;
        this.localeId$ = this.select((state) => state.localeId);
        this.translationDictionaryMapping$ = this.select((state) => state.translationDictionaryMapping);
        this.updateTranslationDictionary = this.updater((state, translationDictionary) => ({
            ...state,
            translationDictionary,
        }))(combineLatest([this.localeId$, this.translationDictionaryMapping$]).pipe(map$1(([localeId, translationDictionaryMapping]) => translationDictionaryMapping[localeId])));
        this.updateTranslationDictionaryMapping = this.updater((state, translationDictionaryMapping) => ({
            ...state,
            translationDictionaryMapping,
        }));
    }
    registerTranslationDictionaryMapping(...translationDictionaryMapping) {
        this.updateTranslationDictionaryMapping(mergeTranslationDictionaryMappings(this.translationDictionaryMapping, ...translationDictionaryMapping));
    }
    get translationDictionary() {
        return this.get((state) => state.translationDictionary);
    }
    get translationDictionaryMapping() {
        return this.get((state) => state.translationDictionaryMapping);
    }
}
TranslationDictionaryStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslationDictionaryStore, deps: [{ token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Injectable });
TranslationDictionaryStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslationDictionaryStore });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslationDictionaryStore, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: LocaleId, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; } });

class TranslateService {
    constructor(translationDictionaryMapping, translationDictionaryStore) {
        this.translationDictionaryMapping = translationDictionaryMapping;
        this.translationDictionaryStore = translationDictionaryStore;
        this.translationDictionaryStore.registerTranslationDictionaryMapping(...this.translationDictionaryMapping);
    }
    translate(translationId, interpolationData) {
        const translationDictionary = this.translationDictionaryStore.translationDictionary;
        const translation = translationDictionary && translationDictionary[translationId];
        if (!translation) {
            console.error(`Translation not found for id: ${translationId.toString()}`);
            return translationId.toString();
        }
        return interpolate(translation, interpolationData);
    }
}
TranslateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslateService, deps: [{ token: TRANSLATION_DICTIONARY_MAPPING }, { token: TranslationDictionaryStore }], target: i0.ɵɵFactoryTarget.Injectable });
TranslateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslateService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslateService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [TRANSLATION_DICTIONARY_MAPPING]
                }] }, { type: TranslationDictionaryStore }]; } });

class TranslatePipe {
    constructor(translateService) {
        this.translateService = translateService;
    }
    transform(translationId, interpolationData) {
        return this.translateService.translate(translationId, interpolationData);
    }
}
TranslatePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslatePipe, deps: [{ token: TranslateService }], target: i0.ɵɵFactoryTarget.Pipe });
TranslatePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: TranslatePipe, name: "translate" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'translate',
                }]
        }], ctorParameters: function () { return [{ type: TranslateService }]; } });

const COUNTRY_ISO_2_CODE = new InjectionToken('Country ISO 2 code');

const COUNTRY_ISO_3_CODE = new InjectionToken('Country ISO 3 code');

const COUNTRY_NAME = new InjectionToken('Country Name');

const IS_GDPR_LOCALE = new InjectionToken('Is GDPR Locale');

class DgxI18nModule {
    static forRoot(config) {
        return {
            ngModule: DgxI18nModule,
            providers: [
                {
                    provide: COUNTRY_ISO_NUMBER,
                    useFactory: () => countryIsoNumberFactory(config?.countryIsoNumber),
                },
                {
                    provide: COUNTRY_ISO_2_CODE,
                    useFactory: countryIso2CodeFactory,
                    deps: [COUNTRY_ISO_NUMBER],
                },
                {
                    provide: COUNTRY_ISO_3_CODE,
                    useFactory: countryIso3CodeFactory,
                    deps: [COUNTRY_ISO_NUMBER],
                },
                {
                    provide: COUNTRY_NAME,
                    useFactory: countryNameFactory,
                    deps: [COUNTRY_ISO_NUMBER],
                },
                {
                    provide: DEFAULT_CURRENCY_CODE,
                    useFactory: defaultCurrencyCodeFactory,
                    deps: [COUNTRY_ISO_NUMBER],
                },
                {
                    provide: DEFAULT_DATE_FORMAT,
                    useFactory: defaultDateFormatFactory,
                    deps: [COUNTRY_ISO_NUMBER],
                },
                {
                    provide: IS_GDPR_LOCALE,
                    useFactory: isGdprLocaleFactory,
                    deps: [COUNTRY_ISO_NUMBER],
                },
                {
                    provide: LOCALE_ID,
                    useFactory: (countryIsoNumber) => localeIdFactory(countryIsoNumber, config?.localeId),
                    deps: [COUNTRY_ISO_NUMBER],
                },
                {
                    provide: PROVINCE_NAMES,
                    useFactory: provinceNamesFactory,
                    deps: [COUNTRY_ISO_NUMBER],
                },
                provideTranslationDictionaryMapping(config?.translationDictionaryMapping),
                TranslationDictionaryStore,
            ],
        };
    }
    static forChild(translationDictionaryMapping) {
        return {
            ngModule: DgxI18nModule,
            providers: [provideTranslationDictionaryMapping(translationDictionaryMapping)],
        };
    }
}
DgxI18nModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxI18nModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DgxI18nModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: DgxI18nModule, declarations: [ConjunctionPipe,
        CountryIsoNumberDirective,
        CurrencyFormatPipe,
        DateFormatPipe,
        ProvinceCodePipe,
        ProvinceNamePipe,
        TranslatePipe], imports: [CommonModule], exports: [ConjunctionPipe,
        CountryIsoNumberDirective,
        CurrencyFormatPipe,
        DateFormatPipe,
        ProvinceCodePipe,
        ProvinceNamePipe,
        TranslatePipe] });
DgxI18nModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxI18nModule, providers: [I18nService, TranslateService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxI18nModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        ConjunctionPipe,
                        CountryIsoNumberDirective,
                        CurrencyFormatPipe,
                        DateFormatPipe,
                        ProvinceCodePipe,
                        ProvinceNamePipe,
                        TranslatePipe,
                    ],
                    exports: [
                        ConjunctionPipe,
                        CountryIsoNumberDirective,
                        CurrencyFormatPipe,
                        DateFormatPipe,
                        ProvinceCodePipe,
                        ProvinceNamePipe,
                        TranslatePipe,
                    ],
                    providers: [I18nService, TranslateService],
                }]
        }] });

const mockI18nModuleConfig = {
    countryIsoNumber: CountryIsoNumber.GreatBritain,
};

const mockTranslationDictionary = {
    TEST_STRING: 'test string value',
    TEST_INTERPOLATED_STRING: '{{ interpolationValue }}',
};

const mockTranslationDictionaryMapping = {
    [LocaleId.English_GreatBritain]: mockTranslationDictionary,
};

/**
 * Generated bundle index. Do not edit.
 */

export { COUNTRY_ISO_2_CODE, COUNTRY_ISO_3_CODE, COUNTRY_ISO_NUMBER, COUNTRY_NAME, ConjunctionPipe, CountryDateFormat, CountryIso2Code, CountryIso3Code, CountryIsoNumber, CountryIsoNumberDirective, CountryName, CountryPostalCodeFormat, CountryTelephoneFormat, CurrencyCode, CurrencyFormatPipe, DEFAULT_DATE_FORMAT, DateFormatPipe, DgxI18nModule, I18nService, IEProvinceCode, IEProvinceName, IS_GDPR_LOCALE, LocaleId, PROVINCE_NAMES, ProvinceCodePipe, ProvinceNamePipe, TRANSLATION_DICTIONARY_MAPPING, TranslatePipe, TranslateService, TranslationDictionaryStore, USProvinceCode, USProvinceName, buildConjunction, countryIso2CodeFactory, countryIso3CodeFactory, countryIsoNumberFactory, countryNameFactory, formatCurrency, formatProvinceCode, formatProvinceName, mergeTranslationDictionaries, mergeTranslationDictionaryMappings, mockI18nModuleConfig, mockTranslationDictionary, mockTranslationDictionaryMapping, provideTranslationDictionaryMapping };
